import React, { useState, useEffect, useCallback } from 'react';
 import { Modal, Button, Form, Row, Col, Spinner, Alert, Badge } from 'react-bootstrap';
import DatePicker, { registerLocale } from 'react-datepicker';
import ptBR from 'date-fns/locale/pt-BR';
import axios from 'axios';
import { useEntidadeContext } from '../../EntidadeContext';
import { useAlertModal } from '../../AlertModalContext';

registerLocale('pt-BR', ptBR);

const AgendamentoLeadModal = ({ show, onHide, paciente, lead, especialidades, todosMedicos, servicos, agendaConfig, onAppointmentCreated, onLeadUpdated }) => {
    const { currentEntidade } = useEntidadeContext();
    const { showAlertModal } = useAlertModal();

    const [step, setStep] = useState(1); // 1: Selecionar Profissional, 2: Selecionar Horário
    const [selectedEspecialidade, setSelectedEspecialidade] = useState('');
    const [selectedMedico, setSelectedMedico] = useState('');
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [availableSlots, setAvailableSlots] = useState([]);
    const [isLoadingSlots, setIsLoadingSlots] = useState(false);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [isScheduling, setIsScheduling] = useState(false);
    const [selectedServicos, setSelectedServicos] = useState([]); // NOVO: Estado para serviços

    // Garante que todosMedicos seja um array antes de filtrar
    const medicosFiltrados = Array.isArray(todosMedicos)
        ? todosMedicos.filter(m => m.especialidade_id === parseInt(selectedEspecialidade, 10))
        : [];

    const resetState = useCallback(() => {
        setStep(1);
        setSelectedEspecialidade('');
        setSelectedMedico('');
        setSelectedDate(new Date());
        setAvailableSlots([]);
        setSelectedSlot(null);
        setIsScheduling(false);
        setSelectedServicos([]); // Limpa os serviços selecionados
    }, []);

    useEffect(() => {
        if (show) {
            resetState();
        }
    }, [show, resetState]);

    const handleDateChange = (date) => {
        setSelectedDate(date);
        setSelectedSlot(null);
        setAvailableSlots([]);
    };

    // NOVO: Handler para a seleção de serviços
    const handleServicoChange = (servicoId) => {
        setSelectedServicos(prev =>
            prev.includes(servicoId)
                ? prev.filter(id => id !== servicoId)
                : [...prev, servicoId]);
    };

    const fetchAvailableSlots = async () => {
        if (!selectedMedico || !selectedDate || !currentEntidade) return;

        setIsLoadingSlots(true);
        setAvailableSlots([]);
        setSelectedSlot(null);

        // CORREÇÃO: Formata a data manualmente para evitar problemas de fuso horário.
        // Isso garante que a data enviada para a API seja sempre a data selecionada no calendário.
        const year = selectedDate.getFullYear();
        const month = String(selectedDate.getMonth() + 1).padStart(2, '0'); // Adiciona zero à esquerda se necessário
        const day = String(selectedDate.getDate()).padStart(2, '0'); // Adiciona zero à esquerda se necessário
        const formattedDate = `${year}-${month}-${day}`;

        try {
            // 1. Buscar os horários já agendados para o médico na data
            const response = await axios.get(`/api/agendamentos/entidade/${currentEntidade.id}/data/${formattedDate}`);
            const bookedTimes = new Set((response.data.agendamentos || []).map(app => app.hora_marcada.slice(0, 5)));

            // 2. Gerar todos os horários possíveis para o dia com base na configuração da agenda
            const diaDaSemana = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sab'][selectedDate.getDay()];
            const configDia = agendaConfig?.horarios?.[diaDaSemana];
            const duracaoPadrao = agendaConfig?.duracao_padrao || 30;

            if (!configDia || !configDia.ativo) {
                setAvailableSlots([]);
                return;
            }

            const allPossibleSlots = [];
            const periodos = ['manha', 'tarde', 'noite'];
            periodos.forEach(periodo => {
                const inicioStr = configDia[`${periodo}_inicio`];
                const fimStr = configDia[`${periodo}_fim`];
                if (inicioStr && fimStr) {
                    let currentTime = new Date(`1970-01-01T${inicioStr}`);
                    const endTime = new Date(`1970-01-01T${fimStr}`);

                    while (currentTime < endTime) {
                        allPossibleSlots.push(currentTime.toTimeString().slice(0, 5));
                        currentTime.setMinutes(currentTime.getMinutes() + duracaoPadrao);
                    }
                }
            });

            // 3. Filtrar os horários possíveis, removendo os que já estão agendados
            const available = allPossibleSlots.filter(slot => !bookedTimes.has(slot));
            setAvailableSlots(available);

        } catch (error) {
            // CORREÇÃO: Se a API falhar, não exibe nenhum horário e mostra um alerta.
            console.error("Erro ao buscar horários:", error);
            showAlertModal('Erro de Comunicação', 'Não foi possível verificar os horários disponíveis. Tente novamente.', 'danger');
            setAvailableSlots([]); // Limpa os horários para evitar agendamentos incorretos.
        } finally {
            setIsLoadingSlots(false);
        }
    };

    const handleCreateAppointment = async () => {
        if (!paciente || !selectedMedico || !selectedDate || !selectedSlot) {
            showAlertModal('Validação', 'Todos os campos são obrigatórios para o agendamento.', 'warning');
            return;
        }

        setIsScheduling(true);
        try {
            const appointmentData = {
                entidade_id: currentEntidade.id,
                paciente_id: paciente.id,
                medico_id: selectedMedico,
                especialidade_id: selectedEspecialidade,
                data_agendamento: `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`,
                hora_marcada: selectedSlot,
                tipo: 'Consulta',
                servicos: selectedServicos, // Adiciona os serviços ao payload
                lead_responsavel_id: lead?.responsavel_id || null, // NOVO: Envia o ID do responsável pelo lead
                status: 'Agendada',
            };

            await axios.post('/api/agendamentos', appointmentData);

            // 1. Após agendar, atualiza o lead com o histórico e novo status
            if (lead && lead.id) {
                const medicoAgendado = todosMedicos.find(m => m.id === parseInt(selectedMedico, 10));
                const notaHistorico = `Agendamento realizado para ${selectedDate.toLocaleDateString('pt-BR')} às ${selectedSlot} com o(a) Dr(a). ${medicoAgendado?.nome || 'Não identificado'}.`;

                const leadUpdateResponse = await axios.put(`/api/leads/${lead.id}`, {
                    status: 'convertido',
                    nova_nota: notaHistorico,
                });

                // Notifica a MarketingPage sobre a atualização do lead para recarregar o Kanban
                if (onLeadUpdated) {
                    onLeadUpdated(leadUpdateResponse.data.lead);
                }
            }

            showAlertModal('Sucesso', 'Agendamento realizado com sucesso!', 'success');
            onHide(); // Fecha este modal

        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Ocorreu um erro ao criar o agendamento.';
            showAlertModal('Erro', errorMessage, 'danger');
        } finally {
            setIsScheduling(false);
        }
    };


    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>Agendar para: {paciente?.nome}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row>
                    <Col md={5}>
                        <h5>1. Selecione o Profissional e a Data</h5>
                        <Form.Group className="mb-3">
                            <Form.Label>Especialidade</Form.Label>
                            <Form.Select value={selectedEspecialidade} onChange={(e) => {
                                const newEspecialidadeId = e.target.value;
                                setSelectedEspecialidade(newEspecialidadeId);
                                // Limpa o médico e os horários ao trocar de especialidade
                                setSelectedMedico('');
                                setAvailableSlots([]);
                                setSelectedSlot(null);}}>
                                <option value="">Selecione...</option>
                                {especialidades.map(esp => <option key={esp.id} value={esp.id}>{esp.nome}</option>)}
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Médico</Form.Label>
                            <Form.Select value={selectedMedico} onChange={(e) => setSelectedMedico(e.target.value)} disabled={!selectedEspecialidade}>
                                <option value="">Selecione...</option>
                                {medicosFiltrados.map(med => <option key={med.id} value={med.id}>{med.nome}</option>)}
                            </Form.Select>
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>Data do Agendamento</Form.Label>
                            <DatePicker
                                selected={selectedDate}
                                onChange={handleDateChange}
                                dateFormat="dd/MM/yyyy"
                                locale={ptBR}
                                inline
                                minDate={new Date()}
                            />
                        </Form.Group>
                        <Button onClick={fetchAvailableSlots} disabled={!selectedMedico || isLoadingSlots} className="mt-3 w-100">
                            {isLoadingSlots ? <Spinner size="sm" /> : 'Verificar Horários'}
                        </Button>

                        {/* NOVO: Seção de Serviços */}
                        <Form.Group className="mt-4">
                            <Form.Label as="h5">Serviços</Form.Label>
                            <div className="servicos-container" style={{ maxHeight: '150px', overflowY: 'auto', border: '1px solid #dee2e6', padding: '10px', borderRadius: '5px' }}>
                                {(servicos || []).filter(s => s.ativo).map(servico => (
                                    <Form.Check
                                        key={servico.id}
                                        type="checkbox"
                                        id={`servico-${servico.id}`}
                                        label={`${servico.nome} (R$ ${servico.valor})`}
                                        checked={selectedServicos.includes(servico.id)}
                                        onChange={() => handleServicoChange(servico.id)}
                                    />
                                ))}
                            </div>
                        </Form.Group>
                    </Col>
                    <Col md={7}>
                        <h5>2. Selecione o Horário</h5>
                        {isLoadingSlots && <div className="text-center"><Spinner /></div>}
                        {!isLoadingSlots && availableSlots.length > 0 && (                           
                            <div className="d-flex flex-wrap gap-2 mt-3">
                                {availableSlots.map(slot => (
                                    <Badge
                                        key={slot}
                                        pill
                                        bg={selectedSlot === slot ? 'primary' : 'light'}
                                        text={selectedSlot === slot ? 'white' : 'dark'}
                                        onClick={() => setSelectedSlot(slot)}
                                        style={{ cursor: 'pointer', fontSize: '1rem', padding: '0.5em 0.9em' }}
                                    >
                                        {slot}
                                    </Badge>
                                ))}
                            </div>
                        )}
                        {!isLoadingSlots && availableSlots.length === 0 && (
                             <Alert variant="warning" className="mt-3">
                                Não há horários disponíveis para a data e profissional selecionados.
                            </Alert>
                        )}
                    </Col>
                </Row>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Cancelar
                </Button>
                <Button
                    variant="primary"
                    disabled={!selectedSlot || isScheduling}
                    onClick={handleCreateAppointment}
                >
                    {isScheduling ? <><Spinner size="sm" /> Agendando...</> : 'Confirmar Agendamento'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default AgendamentoLeadModal;
